import { useTasks } from '@app/context/TaskContext';
import { useUser } from '@app/context/UserContext';
import { NavigationSection } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { calculateTopicStatus } from '@app/utils/topicStatus';
import { Button, Icon } from '@components/ui';
import { CloseButton } from '@components/ui/CloseButton/CloseButton';
import { UserTopicApplications } from '@dieterApi/user/useUserQuery';
import { animated, useSpring, useTrail } from '@react-spring/web';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import './status.sass';

export function Status() {
  const { t } = useTranslation();
  const { user, topicsWithoutDpoFilter: topics, whitelabelConfig } = useUserWithCompany();
  const { userValues } = useUserValuesData();
  const tasks = useTasks();
  const { navigateDashboard, navigation } = useNavigation();
  const checkUpTopic = topics.find((topic) => topic.id === 'topic-checkup');
  const internalTopics =
    topics.filter(
      (topic) =>
        topic.tags.some((tag) => tag.tag === 'internal') && topic.tags.every((tag) => tag.tag !== 'experimental') && topic.id !== 'topic-dsfa'
    ) || [];
  const externalTopics =
    topics.filter(
      (topic) =>
        topic.tags.some((tag) => tag.tag === 'external') && topic.tags.every((tag) => tag.tag !== 'experimental')
    ) || [];

  const handleClose = () => {
    navigateDashboard(NavigationSection.Overview);
  };

  const hasWhitelabel = !!whitelabelConfig?.avatarUrl;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/assets/images/dieter_badge_de.svg`;
    link.download = 'dieter_badge_de.svg';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Container entrance animation — fade + slide up
  const containerSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 220, friction: 24 },
  });

  // Build sections array for staggered trail animation
  const sections = [
    { key: 'checkup', element: <StatusSection topics={checkUpTopic ? [checkUpTopic] : []} lines={false} /> },
    { key: 'divider-1', element: <hr className="dashed-2" /> },
    {
      key: 'external',
      element: (
        <StatusSection
          topics={externalTopics}
          description={t('dashboard.status.outer.description')}
          title={t('dashboard.status.outer.title')}
          lines={externalTopics.some(
            (topic) => calculateTopicStatus(topic, undefined, userValues, tasks) === 'COMPLETED'
          )}
        />
      ),
    },
    { key: 'divider-2', element: <hr className="dashed-2" /> },
    {
      key: 'internal',
      element: (
        <StatusSection
          topics={internalTopics}
          description={t('dashboard.status.inner.description')}
          title={t('dashboard.status.inner.title')}
          lines={internalTopics.some(
            (topic) => calculateTopicStatus(topic, undefined, userValues, tasks) === 'COMPLETED'
          )}
        />
      ),
    },
  ];

  const trail = useTrail(sections.length, {
    from: { opacity: 0, transform: 'translateX(30px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    config: { tension: 200, friction: 22 },
    delay: 150,
  });

  return (
    <animated.div className="dtRouteDashboard__status" style={containerSpring}>
      <h3>{t('dashboard.status.header')}</h3>
      <div className="flex gap-5">
        {!hasWhitelabel && (
          <>
            <div className="flex flex-col gap-5 flex-3">
              <p>{t('dashboard.status.description')}</p>
              <img src="/assets/images/dieter_badge_de_temp.png" alt="Info" />
              <div className="flex flex-1 flex-col justify-between">
                <div>
                  <Button disabled={navigation.progress !== 1} onClick={handleDownload}>
                    {t('dashboard.status.download')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex-1" />
          </>
        )}
        <div className={cx({ 'flex-5': !hasWhitelabel, 'flex-1': hasWhitelabel })}>
          {user && (
            <div className="flex flex-col gap-[10px]">
              {trail.map((style, index) => (
                <animated.div key={sections[index].key} style={style}>
                  {sections[index].element}
                </animated.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <CloseButton onClick={handleClose} data-testid="status-close-button" />
    </animated.div>
  );
}

interface Props {
  topics: UserTopicApplications[];
  description?: string;
  title?: string;
  lines: boolean;
}

export function StatusSection({ topics, description, title, lines }: Props) {
  const { user } = useUser();
  const { userValues } = useUserValuesData();
  const tasks = useTasks();
  const completeTopics = topics.filter(
    (topic) => calculateTopicStatus(topic, undefined, userValues, tasks) === 'COMPLETED'
  );
  const incompleteTopics = topics.filter(
    (topic) => calculateTopicStatus(topic, undefined, userValues, tasks) !== 'COMPLETED'
  );

  const topicDisplay = (topic: UserTopicApplications, idx: number) => {
    const topicIsComplete = calculateTopicStatus(topic, undefined, userValues, tasks) === 'COMPLETED';
    // const isFirst = idx === 0;
    return (
      <div className="statusSectionItem flex gap-[10px]" key={topic.id}>
        {/* {lines && topicIsComplete && !isFirst && <div className="vl vl-start" />} */}
        <div
          className={cx('statusSection__indicator flex', {
            indicatorDone: topicIsComplete,
          })}
          data-testid={`status-section-indicator-${topic.id}`}
        >
          <Icon type="check-50" scale={0.45} size={20} />
        </div>
        <div className="flex-1">{topic.title}</div>
      </div>
    );
  };

  return (
    <div className="dtRouteDashboard__statusSection flex gap-5">
      {/* {lines && <div className="vl vl-start" />} */}
      <div className="flex-1">
        <strong>{title}</strong>
        <div className="flex flex-col gap-5 mt-5">{completeTopics.map(topicDisplay)}</div>
        <div className="flex flex-col gap-5 mt-5">{incompleteTopics.map(topicDisplay)}</div>
      </div>
      <div className="flex-1">
        <p>{description}</p>
      </div>
    </div>
  );
}
